import React, { HtmlHTMLAttributes, ReactNode } from 'react';
import { MeiDouDetail } from '../meidouInfo/services';
import { IResponseTeamMember, ITeamIdentity } from './teamInfo';

/**
 * 用户信息数据类型
 *
 * @see https://api-mock.meitu-int.com/project/2110/interface/api/116813
 */
export interface UserInfoType {
    /**
     * uid
     */
    id: number;

    /**
     * 昵称
     */
    screen_name: string;

    /**
     * 头像
     */
    avatar: string;

    /**
     * 会员标识
     */
    is_vip: boolean;

    /**
     * 会员有效期
     */
    vip_end_time: number;

    /**
     * 会员剩余天数
     *
     * - 为 `0` 时表示没充过会员
     */
    vip_days?: number;

    /**
     * 营销文案
     */
    promotionContent?: string;

    /**
     * vmark 用户标识
     */
    vmake_user?: boolean;

    /**
     * 是否已经订阅（签订合约，自动续订）（包括试用中、已订阅）- 海外下发
     */
    is_subscribe?: boolean;

    /**
     * 是否是补偿用户
     */
    is_compensation?: boolean;

    /**
     * 是否试用 - 海外
     *
     * - `is_vip` 为 `false` 才会下发这个字段
     */
    has_trial?: boolean;

    /**
     * 会员等级
     */
    vip_level?: VipLevel | VipLevelIntl;

    /**
     * 教育版用户标识
     */
    education_role?: number;
}

// 国内会员等级枚举
export enum VipLevel {
    /**
     * 基础会员
     */
    Basic = 1,
    /**
     * 秀秀高粉会员（视同基础会员）
     */
    Pro = 2,
    /**
     * 高级会员
     */
    ProCn = 3,
}

// 海外会员等级枚举
export enum VipLevelIntl {
    /**
     * 基础会员
     */
    Basic = 1,
    /**
     * 高级会员
     */
    Pro = 2,
    /**
     * Max 会员
     */
    Max = 3,
}

export enum ListKeyType {
    /**
     * 意见反馈
     */
    FEEDBACK = 'feedBack',
    /**
     * 在线咨询
     */
    BUSINESS = 'business',
    /**
     *
     */
    DOWNLOAD = 'download',

    /** 美豆 */
    CREDIT = 'credit',

    /** 订阅续费管理 */
    ManageSubscriptions = 'manageSubscriptions',

    /**
     * 订阅购买
     */
    Subscription = 'subscription',

    /**
     * 条款与政策
     */
    TermsPolicies = 'termsPolicies',

    /**
     * 套餐和价格
     */
    Pricing = 'pricing',

    /**
     * 切换个人/团队
     */
    ChangeIdentity = 'changeIdentity',

    /**
     * 用户补偿（福利升级）
     */
    Compensation = 'compensation',

    /**
     * 语言切换
     */
    Language = 'language',

    /**
     * 兑换
     */
    Redeem = 'redeem',

    /**
     * 邀请奖励
     */
    Invite = 'invite',

    /**
     * 大账号个人中心（海外版）
     */
    Account = 'account',

    /**
     * 下载客户端
     */
    DOWNLOAD_APP = 'downloadApp',

    /** 团队管理 */
    TEAM_ADMIN = 'teamAdmin',
    /**
     * 邀请奖励（国内）
     */
    InviteCn = 'inviteCn',
}

export interface BalanceList {
    icon: string;
    amount: number;
    name: string;
    key?: string;
    clickable?: boolean;
    handler?: () => void;
    // 充值美豆
    onBuyMeiDou?: () => void;
    // 明细
    onViewDetail?: () => void;
}

export enum BalanceListKeyType {
    /**
     * 美豆
     */
    MEIDOU = 'meidou',
    /**
     * 抠图
     */
    CUTOUT = 'cutout',
}

/**
 * 用户信息组件属性
 */
export interface IUserInfoProps extends HtmlHTMLAttributes<HTMLDivElement> {
    /**
     * 用户信息
     */
    userInfo?: UserInfoType;

    /**
     * 大账号回调地址
     */
    callback: string;

    /**
     * 是否隐藏会员相关信息
     */
    hideVipInfo?: boolean;

    /**
     * 自定义要显示的列表 Key
     */
    listKeys?: ListKeyType[];

    /**
     * 自定义列表选项
     */
    list?: {
        /**
         * 列表 Key
         */
        key: string;

        /**
         * 列表标签
         */
        label: string | ReactNode;

        /**
         * 列表图标组件
         *
         * - 目前只作用于海外版
         */
        icon?: ReactNode;

        /**
         * 列表项点击回调处理方法
         */
        callback?: () => void;

        /**
         * 列表项指定索引顺序
         * 正数索引0为第1个，1为第2个
         * 负数索引转换，-1为倒数第一个，-2为倒数第二个
         * 注意索引是针对组件内部列表插入的位置，不需要考虑自定义列表插入后的索引值
         */
        index?: number;
    }[];

    /**
     * 头像大小
     */
    avatarSize?: number;

    /**
     * 隐藏会员入口
     */
    hideVipIno?: boolean;

    /**
     * 用户面板的桌面端 UI 方式
     */
    panelUIType?: 'default' | 'popover';

    /**
     *  自定义未登录态按钮
     */
    unLoginBtn?: React.ReactNode;

    /**
     * 开通会员文字提示
     */
    notVipTip?: string;

    /**
     * 未登录下的文案
     */
    notLoginText?: string;

    /**
     * 隐藏退出登录按钮
     */
    hideLogoutBtn?: boolean;

    /**
     * 禁止跳转大账号个人中心
     */
    disableJumptoAccount?: boolean;

    /**
     * 大账号退出登录所需要的 `client_id`
     *
     * - 如果没有传默认使用全局 `config` 中的 `client_id`
     */
    clientIdForLogout?: string;

    /**
     * 是否显示复制UID按钮
     */
    showCopyBtn?: boolean;

    /**
     * 自定义其他订阅入口
     */
    customOtherEntrance?: React.ReactNode;

    /**
     * 退出登录
     */
    onLogout?: () => void;

    /**
     * 登录
     */
    onLogin?: () => void;

    /**
     * 打开会员弹窗
     *
     * @param code 开通会员；1:会员 2：美豆|积分
     * @param params 其他参数参数
     */
    onOpenVipModal?: (code?: number, params?: Record<string, any>) => void;

    /**
     * 跳转个人中心回调
     */
    onJump2Center?: () => void;

    /**
     * 点击列表选项回调
     */
    onListItemClick?: (val: string) => void;

    /**
     * 打开福利升级流程弹窗事件回调处理方法
     *
     * - 目前只作用于海外版
     */
    onBenefitsUpgraded?: () => void;

    /**
     * 隐藏余额
     */
    hideBalance?: boolean;

    /**
     * 未登录开通会员提示描述
     */
    notVipTipSubscript?: string;

    /**
     * 是否归属于PC端，主要是真的 pc.meitu.com 站点
     */
    isBelongToPc?: boolean;

    /**
     * 是否rboboneo场景
     */
    isRoboneoScene?: boolean;

    /**
     * 是否隐藏套餐价格页入口
     */
    hidePricingEntrance?: boolean;

    /**
     * 美豆剩余颗数
     */
    meidouAmount?: number;

    /**
     * 美豆 / 美叶详情
     *
     * @default undefined
     * @see https://api-mock.meitu-int.com/project/2110/interface/api/136229
     */
    meidouDetail?: MeiDouDetail;

    /**
     * 是否请求接口获取美豆 / 美叶详情
     *
     * - 目前只作用于海外版；
     * - 如果传入 `meidouDetail` 属性，则使用 `meidouDetail` 属性值；
     *
     * @default false
     */
    fetchMeidouDetail?: boolean;

    /**
     * 抠图剩余张数
     */
    cutoutAmount?: number;

    /**
     * 仅 Demo 模拟数据使用
     */
    member?: IResponseTeamMember;

    /**
     * 上报埋点参数回调处理方法
     */
    track?: (name: string, option: Record<string, any>) => void;

    /**
     * 取消订阅成功回调
     */
    onUnsubscribeSuccess?: () => void;

    /**
     * 是否使用手机版本
     */
    useMobile?: boolean;

    /**
     * 强制点头像打开登录弹窗
     */
    forceLoginOnAvatarClick?: boolean;

    /**
     * 兑换会员成功
     */
    onExchangeSuccess?: () => void;

    /**
     * 自定义打开大账号个人中心
     */
    onJumpToAccount?: (options: {
        prefix: string;
        url: string;
        client_callback: string;
        client_id: string;
    }) => Promise<void>;

    /**
     * 移动端面板打开回调
     */
    onMobilePanelOpen?: (val: boolean) => void;

    /**
     * 是否显示语言切换
     */
    showSwitchLanguage?: boolean;

    /**
     * 切换语言后是否在路径加上语言前缀
     */
    isAddURLLanguagePrefix?: boolean;

    /**
     * 是否展示邀请奖励
     */
    showInviteReward?: boolean;

    /**
     * 是否显示用户名
     */
    showUserName?: boolean;
}

/**
 * 用户类型
 */
export enum UserType {
    /**
     * 个人用户
     */
    PERSONAL = 'personal',
    /**
     * 团队
     */
    TEAM = 'team',
    /**
     * 公司
     */
    COMPANY = 'company',
}

export interface UserDataContextProps extends IUserInfoProps {
    teamInfo: IResponseTeamMember | undefined;
    currentTeamIdentity?: ITeamIdentity | null;
    handleOpenVipModal: () => void;
    isLogin: boolean;
    handleLogout: () => void;
    handleClick: () => void;
    setFeedbackModalOpen: (open: boolean) => void;
    setManageSubscriptionsModalOpen: (open: boolean) => void;
    setDownloadAppModalOpen: (open: boolean) => void;

    // 当前的显示语言
    language: string;

    /**
     * @param open 是否打开
     */
    setExchangeModalOpen: (open: boolean) => void;

    onCutoutAmountClick: () => void;
    onMeidouAmountClick: () => void;
    onBuyMeiDouClick: () => void;
}
